import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";
import TakeWebCamPicture from "./TakeWebCamPicture";
import { isValidPhone, isValidName } from "../../Services/validation/validationUtiles";
import personIconPng from "../../assets/images/person_icon.png";
import passportIconPng from "../../assets/images/passport_icon.png";
import ticketIconPng from "../../assets/images/ticket.png";
import adminServices from "../../Services/adminService";
import visaService from "../../Services/visaService";
import "../../assets/css/groupForm.css";
import config from "../../config";
import { photoPopUpHtml, maritalStatus } from "./formUtils";
import { isFileImage, isFileSizeWithinLimit, isObjectNotEmpty, capitalizeFirstLetter } from "../../utils";

const formInputs = [
  { label: "שם מלא", name: "fullName" },
  { label: "טלפון", name: "phone", type: "tel" },
  { label: "כתובת מגורים מלאה", name: "address" },
  { label: "עיר לידה", name: "birthCity" },
  { label: "ארץ לידה", name: "birthCountry" },
  { label: "מצב משפחתי", name: "maritalStatus", select: true },
  { label: "שם בן/בת זוג", name: "spouseName", showWhenMarried: true },
  { label: "מקום עבודה ומקצוע", name: "workplaceAndProfession" },
  { label: "תאריך כניסה לטנזניה/זנזיבר", name: "entryDate", type: "date" }
];

const defaultFormData = {
  fullName: "",
  phone: "",
  address: "",
  birthCity: "",
  birthCountry: "",
  maritalStatus: "single",
  spouseName: "",
  workplaceAndProfession: "",
  entryDate: "",
  pictureOfPerson: null,
  pictureOfPassport: null,
  flightTicket: null
};

function TanzaniaForm() {
  let { organizationName, destination, dateOfTrip, groupId, groupAgentName } = useParams();
  organizationName = decodeURIComponent(organizationName);
  destination = decodeURIComponent(destination);
  groupAgentName = decodeURIComponent(groupAgentName);

  const [formData, setFormData] = useState(defaultFormData);
  const [formatErrors, setFormatErrors] = useState({});
  const [takePictureType, setTakePictureType] = useState(null);
  const formQuestions = useRef();

  const fileInputs = [
    { label: "תמונת דרכון", icon: passportIconPng, name: "pictureOfPassport", ref: useRef() },
    { label: "תמונת פספורט", icon: personIconPng, name: "pictureOfPerson", ref: useRef() },
    { label: "כרטיס טיסה", icon: ticketIconPng, name: "flightTicket", ref: useRef() }
  ];

  const onChange = ({ target: { name, value } }) => {
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[name];
      return { ...prevFormatErrors };
    });

    if (name === "maritalStatus" && value !== "married") {
      setFormData({ ...formData, [name]: value, spouseName: "" });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const onChangeFile = ({ target: { name, files } }) => {
    const file = files?.[0];
    if (!file) return;
    if (!isFileImage(file)) {
      Swal.fire("אופס,", "PDF ניתן להעלות רק תמונות או קבצי", "error");
      return;
    }
    if (!isFileSizeWithinLimit(file)) {
      Swal.fire("אופס,", "גודל הקובץ גדול מדי. ניתן להעלות עד 2.5MB", "error");
      return;
    }
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[name];
      return { ...prevFormatErrors };
    });
    setFormData({ ...formData, [name]: file });
  };

  const savePictureFromCamera = (pictureType, imgSrc) => {
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[pictureType];
      return { ...prevFormatErrors };
    });
    setFormData({ ...formData, [pictureType]: imgSrc });
  };

  const onFileInputClick = async (name, inputRef) => {
    if (name === "flightTicket") {
      inputRef.current.click();
      return;
    }

    const { isConfirmed } = await Swal.fire({
      html: photoPopUpHtml(name),
      confirmButtonText: "להמשיך",
      cancelButtonText: "ביטול",
      showCancelButton: true,
      showCloseButton: true
    });
    if (!isConfirmed) return;
    if (!config.isMobile) {
      const userResponse = await Swal.fire({
        icon: "question",
        text: "האם תרצ/י להתצטלם בעזרת המצלמה המחוברת למחשב/לפטופ או להעלות קובץ תמונה מהמחשב?",
        confirmButtonText: "לצלם תמונה",
        cancelButtonText: "להעלות תמונה",
        showCancelButton: true,
        showCloseButton: true
      });

      if (userResponse.value) {
        setTakePictureType(name);
      } else if (userResponse.dismiss === "cancel") {
        inputRef.current.click();
      }
    } else {
      inputRef.current.click();
    }
  };

  const validateForm = (data) => {
    const formatErrors = {};

    if (!isValidName(data.fullName)) {
      formatErrors.fullName = "שם מלא לא תקין";
    }

    if (!isValidPhone(data.phone)) {
      formatErrors.phone = "מספר טלפון לא תקין";
    }

    if (!data.address) {
      formatErrors.address = "נא למלא כתובת";
    }

    if (!isValidName(data.birthCity)) {
      formatErrors.birthCity = "עיר לידה לא תקינה";
    }

    if (!isValidName(data.birthCountry)) {
      formatErrors.birthCountry = "ארץ לידה לא תקינה";
    }

    if (!data.workplaceAndProfession) {
      formatErrors.workplaceAndProfession = "נא למלא מקום עבודה ומקצוע";
    }

    if (!data.entryDate) {
      formatErrors.entryDate = "נא למלא תאריך כניסה";
    }

    if (data.maritalStatus === "married" && !isValidName(data.spouseName)) {
      formatErrors.spouseName = "שם בן/בת זוג לא תקין";
    }

    if (!data.pictureOfPerson) {
      formatErrors.pictureOfPerson = true;
    }

    if (!data.pictureOfPassport) {
      formatErrors.pictureOfPassport = true;
    }

    if (!data.flightTicket) {
      formatErrors.flightTicket = true;
    }

    setFormatErrors(formatErrors);
    return formatErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formatErrors = validateForm(formData);
    if (isObjectNotEmpty(formatErrors)) {
      formQuestions.current.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const form = new FormData();
    for (const file in formData) {
      form.append(file, formData[file]);
    }
    form.append("groupId", groupId);

    const addParticipantRes = await adminServices.addParticipantToGroup(form);
    const newGroupVisaRes = await visaService.createNewGroupVisa({
      visaData: {
        ...formData,
        destination: capitalizeFirstLetter(destination),
        pictureOfPassport: addParticipantRes.data.pictureOfPassportFileName,
        pictureOfPerson: addParticipantRes.data.pictureOfPersonFileName,
        flightTicket: addParticipantRes.data.flightTicketFileName
      }
    });

    if (addParticipantRes?.success === true && newGroupVisaRes?.success === true) {
      setFormData(defaultFormData);
      Swal.fire("בוצע בהצלחה", "הטופס נשלח בהצלחה", "success");
    }
  };

  return (
    <div>
      <Header groupAgentName={groupAgentName} organizationName={organizationName} dateOfTrip={dateOfTrip} destination={destination} />

      <form className="group-form-form" onSubmit={handleSubmit} ref={formQuestions}>
        <h2 className="group-form-title">הכל כמעט מוכן לבואכם</h2>

        <div className="group-form-inputs-container">
          {formInputs.map(({ label, name, select, showWhenMarried, type }) => {
            if (showWhenMarried && formData.maritalStatus !== "married") return null;
            return (
              <label key={name} className="group-form-input-label">
                {label}:
                {select ? (
                  <select className="group-form-input" value={formData[name]} name={name} onChange={onChange}>
                    {maritalStatus.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="group-form-input"
                    value={formData[name]}
                    name={name}
                    type={type || "text"}
                    placeholder={label}
                    onChange={onChange}
                  />
                )}
                {formatErrors[name] && <p className="text-format-error">* {formatErrors[name]}</p>}
              </label>
            );
          })}
        </div>

        <div className="group-form-inputs-container mb-20">
          {fileInputs.map(({ label, icon, name, ref }) => (
            <div key={name} className="group-form-file-input-container">
              <img src={icon} alt="icon" />

              <span className="group-form-file-input-text">
                <p className="group-form-file-input-text-label">{label}</p>
                {formData[name] ? (
                  <p className="group-form-file-input-text-success">מסמך צורף</p>
                ) : (
                  <p className={"group-form-file-input-text-falsy " + (formatErrors[name] ? "file-input-error" : "")}>
                    שום מסמך לא צורף
                  </p>
                )}
              </span>

              <button
                className="group-form-file-input-button"
                type="button"
                onClick={() => {
                  onFileInputClick(name, ref);
                }}
              >
                {formData[name] ? <>&#10004;</> : "צרף"}
              </button>

              <input hidden ref={ref} name={name} type="file" onChange={onChangeFile} />
            </div>
          ))}
        </div>
        <button className="submit-button" type="submit" value="Submit">
          שלח
        </button>
      </form>

      <TakeWebCamPicture
        takePictureType={takePictureType}
        setTakePictureType={setTakePictureType}
        savePictureFromCamera={savePictureFromCamera}
      />
    </div>
  );
}

export default TanzaniaForm;
